import React, {
  useState,
  useMemo,
  useEffect,
} from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getPaginationRowModel,
  createColumnHelper,
  getSortedRowModel,
} from '@tanstack/react-table';
import {
  Table,
  Form,
  Button,
  Dropdown,
  Container,
  Row,
  Col,
  Modal,
} from 'react-bootstrap';
import { useUser } from "../context/UserContext";
import { useAuth } from "react-oidc-context";
import { userManagementAPI } from '../utils/apiClient';
import SuccessToast from './SuccessToast';
import ConfirmModal from './ConfirmModal';
import { trackUserManagement } from '../utils/analytics';

const UserTable = ({ data = [], isLoading = false }) => {
  const auth = useAuth();
  const { userData } = useUser();
  
  // State for selected rows
  const [selectedRows, setSelectedRows] = useState({});
  
  // State for pagination and sorting
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState([]);

  // State for success toast
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // State for confirmation modal
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  
  // State for warning modal (LostLogic protection)
  const [showWarning, setShowWarning] = useState(false);

  // Clear selected rows when data changes
  useEffect(() => {
    setSelectedRows({});
  }, [data]);

  // Helper function to check if name contains "(LostLogic)"
  const isLostLogicUser = (name) => {
    return name && name.includes('(LostLogic)');
  };

  // Handle remove action from dropdown menu
  const handleRemoveTableButton = async ({ gsc_user, gsc_property, action, userName }) => {
    // Check if trying to remove LostLogic user
    if (isLostLogicUser(userName)) {
      setShowWarning(true);
      return;
    }
    
    const token = auth.user?.access_token;
  
    if (!token) {
      console.error("Authorization token is missing");
      return;
    }
  
    const requestData = {
      gsc_user,
      gsc_property,
      action,
      org: userData.o,
      requestor: auth.user?.profile.sub,
    };
  
    try {
      const response = await userManagementAPI.addUsers(token, [requestData]);
      console.log("Response from API:", response);

      // Track the user management event in analytics
      const recordsAffected = response.records_created || requestData.length;
      trackUserManagement('remove_user', recordsAffected);

      setToastMessage(`User ${gsc_user} removal request submitted successfully`);
      setShowToast(true);
    } catch (error) {
      console.error("Error sending data to API:", error);
      alert(`Failed to submit removal request: ${error.message}`);
    }
  };

  // Handle bulk remove from properties
  const handleRemoveFromProperties = async () => {
    const token = auth.user?.access_token;
    
    if (!token) {
      console.error("Authorization token is missing");
      return;
    }
  
    const requestData = [];
    let hasLostLogicUser = false;
  
    // Collect data for selected rows and check for LostLogic users
    Object.keys(selectedRows).forEach((rowId) => {
      if (selectedRows[rowId]) {
        const row = table.getRow(rowId);
        
        // Check if this is a LostLogic user
        if (isLostLogicUser(row.original.name)) {
          hasLostLogicUser = true;
        } else {
          requestData.push({
            gsc_user: row.original.email,
            gsc_property: row.original.property,
            action: 'remove from property',
            org: userData.o,
            requestor: auth.user?.profile.sub
          });
        }
      }
    });
    
    // If any LostLogic users are selected, show warning and don't proceed
    if (hasLostLogicUser) {
      setShowWarning(true);
      return; // Keep selection, don't execute API call
    }
  
    if (requestData.length === 0) {
      alert("Please select at least one row");
      return;
    }

    // Show confirmation modal
    setConfirmAction(() => async () => {
      try {
        const response = await userManagementAPI.addUsers(token, requestData);
        console.log('Response from API:', response);

        // Track the user management event in analytics
        const recordsAffected = response.records_created || requestData.length;
        trackUserManagement('remove_users', recordsAffected);

        setToastMessage(`Successfully submitted removal request for ${requestData.length} user(s)`);
        setShowToast(true);
        setSelectedRows({}); // Clear selection
      } catch (error) {
        console.error('Error sending data to API:', error);
        alert(`Failed to submit removal request: ${error.message}`);
      }
    });
    setShowConfirm(true);
  };

  // Column Definitions with sorting and custom widths
  const columnHelper = createColumnHelper();
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'selection',
        header: ({ table }) => (
          <div style={{ textAlign: 'center', position: 'relative', left: '30%', display: 'list-item' }} className="hideLst">
            <Form.Check
              type="checkbox"
              onChange={(e) => {
                if (e.target.checked) {
                  const newSelectedRows = {};
                  table.getRowModel().rows.forEach((row) => {
                    newSelectedRows[row.id] = true;
                  });
                  setSelectedRows(newSelectedRows);
                } else {
                  setSelectedRows({});
                }
              }}
              checked={
                Object.keys(selectedRows).length === table.getRowModel().rows.length &&
                table.getRowModel().rows.length > 0
              }
            />
          </div>
        ),
        cell: (info) => (
          <div style={{ textAlign: 'center', position: 'relative', left: '25%', display: 'list-item' }} className="hideLst">
            <Form.Check
              type="checkbox"
              checked={!!selectedRows[info.row.id]}
              onChange={(e) => {
                setSelectedRows((prev) => ({
                  ...prev,
                  [info.row.id]: e.target.checked,
                }));
              }}
            />
          </div>
        ),
        size: 50,
        enableSorting: false,
      }),
      columnHelper.accessor('name', { 
        header: 'Name',
        size: 150,
        enableSorting: true,
      }),
      columnHelper.accessor('email', { 
        header: 'Email',
        size: 250,
        enableSorting: true,
      }),
      columnHelper.accessor('permissions', { 
        header: 'Permissions',
        size: 120,
        enableSorting: true,
      }),
      columnHelper.accessor('property', { 
        header: 'Property',
        size: 250,
        enableSorting: true,
      }),
      columnHelper.accessor('domain', { 
        header: 'Domain',
        size: 150,
        enableSorting: true,
      }),
      columnHelper.accessor('discovered', { 
        header: 'Discovered',
        size: 120,
        enableSorting: true,
      }),
      columnHelper.accessor('added_by', { 
        header: 'Added By',
        size: 150,
        enableSorting: true,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Remove',
        size: 120,
        enableSorting: false,
        cell: (info) => (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Dropdown>
              <Dropdown.Toggle variant="secondary" size="sm" id={`dropdown-${info.row.id}`}>
                Remove
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveTableButton({
                      gsc_user: info.row.original.email,
                      gsc_property: info.row.original.property,
                      action: "remove from property",
                      userName: info.row.original.name,
                    });
                  }}
                >
                  From Property
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveTableButton({
                      gsc_user: info.row.original.email,
                      gsc_property: info.row.original.domain,
                      action: "remove from domain",
                      userName: info.row.original.name,
                    });
                  }}
                >
                  From Domain
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveTableButton({
                      gsc_user: info.row.original.email,
                      gsc_property: 'all',
                      action: "remove from account",
                      userName: info.row.original.name,
                    });
                  }}
                >
                  From Account
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        ),
      }),
    ],
    [columnHelper, selectedRows]
  );

  // TanStack Table Instance
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: {
      pagination,
      sorting,
    },
  });

  const pageSizes = [10, 25, 50, 100, 250, 500];

  // Show empty state
  if (!data || data.length === 0) {
    return (
      <Container className="text-center mt-5">
        <p className="text-muted">No data available. Try selecting a different date or adjusting filters.</p>
      </Container>
    );
  }

  return (
    <Container fluid style={{ paddingLeft: '15px', paddingRight: '15px' }}>
      <Row className="mb-3 align-items-center">
        <Col xs={12} md={6} className="mb-2 mb-md-0">
          <div className="d-flex align-items-center">
            <span className="me-2 text-muted">Show &nbsp;</span>
            <Form.Select
            value={pagination.pageSize}
            onChange={(e) => {
              setPagination({
                pageIndex: 0,
                pageSize: Number(e.target.value),
              });
            }}
            style={{ width: 'auto'}}
          >
            {pageSizes.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Form.Select>
            <span className="ms-2 text-muted">&nbsp; entries</span>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="d-flex justify-content-md-end">
            <span className="me-3 align-self-center text-muted">
              Page {pagination.pageIndex + 1} of {table.getPageCount()}&nbsp;&nbsp;
            </span>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>&nbsp;
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </Col>
      </Row>

      <div style={{ width: '100%', overflowX: 'auto', paddingBottom: '110px' }}>
        <Table striped bordered hover id="list-user-table" style={{ 
          width: '100%', 
          tableLayout: 'fixed',
          minWidth: '1200px'
        }}>
          <thead style={{ backgroundColor: '#343a40', color: 'white' }}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th 
                    key={header.id}
                    style={{ 
                      width: header.column.columnDef.size ? `${header.column.columnDef.size}px` : 'auto',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      cursor: header.column.getCanSort() ? 'pointer' : 'default',
                      userSelect: 'none',
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <span style={{ fontSize: '0.8em' }}>
                          {header.column.getIsSorted() === 'asc' ? '▲' : 
                           header.column.getIsSorted() === 'desc' ? '▼' : 
                           '⇅'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => {
                  const isActionsColumn = cell.column.id === 'actions';
                  
                  return (
                    <td 
                      key={cell.id}
                      style={{
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        overflow: isActionsColumn ? 'visible' : 'hidden',
                        textOverflow: isActionsColumn ? 'clip' : 'ellipsis',
                        whiteSpace: isActionsColumn ? 'normal' : 'nowrap',
                        padding: '8px',
                        position: isActionsColumn ? 'relative' : 'static',
                      }}
                      title={!isActionsColumn ? (cell.getValue()?.toString() || '') : ''}
                    >
                      {isActionsColumn ? (
                        flexRender(cell.column.columnDef.cell, cell.getContext())
                      ) : (
                        <div style={{
                          overflow: 'auto',
                          maxHeight: '60px',
                          textAlign: 'center',
                        }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <Row className="mt-3">
        <Col>
          <Button 
            id="remove-from-properties-btn"
            variant="danger" 
            onClick={handleRemoveFromProperties}
            disabled={Object.keys(selectedRows).length === 0}
          >
            Remove from Properties ({Object.keys(selectedRows).filter(k => selectedRows[k]).length})
          </Button>
        </Col>
      </Row>

      <Row className="mt-3 text-muted" style={{textAlign: 'justify'}}>
        <Col>
          <span>
            Page {pagination.pageIndex + 1} of {table.getPageCount()} | 
            Showing {table.getRowModel().rows.length} of {data.length} results
          </span>
        </Col>
        <Col className="d-flex justify-content-end">
          <Button
            variant="outline-primary"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>&nbsp;
          <Button
            variant="outline-primary"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </Col>
      </Row>
      
      <SuccessToast 
        show={showToast}
        onHide={() => setShowToast(false)}
        message={toastMessage}
        duration={4000}
      />
      
      <ConfirmModal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        onConfirm={confirmAction}
        title="Confirm Removal"
        message={`Are you sure you want to remove ${Object.keys(selectedRows).filter(k => selectedRows[k]).length} user(s) from their properties?`}
        confirmText="Remove"
        cancelText="Cancel"
      />
      
      {/* Warning Modal for LostLogic Protection */}
      {/* <Modal show={showWarning} onHide={() => setShowWarning(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-exclamation-triangle text-warning me-2"></i>
            Google Search Console Alert
          </Modal.Title>
        </Modal.Header> */}
        <Modal show={showWarning} onHide={() => setShowWarning(false)} size="lg" centered>
          <Modal.Header>
            <Modal.Title>
              <i className="fas fa-exclamation-triangle text-warning me-2"></i>
              Google Search Console Alert
            </Modal.Title>
            <Button 
              variant="link" 
              onClick={() => setShowWarning(false)} 
              style={{ 
                position: "absolute", 
                right: "10px", 
                top: "10px", 
                fontWeight: "500", 
                fontSize: "medium",
                textDecoration: "none",
                color: "#000"
              }}
            >
              ×
            </Button>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-0">
              <strong>You can not remove your own access</strong>
            </p>
            <p className="mt-2 text-muted">
              Please deselect any users marked with "(LostLogic)" and try again.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setShowWarning(false)}>
              OK
            </Button>
          </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UserTable;



// import React, {
//   useState,
//   useMemo,
//   useEffect,
// } from 'react';
// import {
//   useReactTable,
//   getCoreRowModel,
//   flexRender,
//   getPaginationRowModel,
//   createColumnHelper,
//   getSortedRowModel,
// } from '@tanstack/react-table';
// import {
//   Table,
//   Form,
//   Button,
//   Dropdown,
//   Container,
//   Row,
//   Col,
// } from 'react-bootstrap';
// import { useUser } from "../context/UserContext";
// import { useAuth } from "react-oidc-context";
// import { userManagementAPI } from '../utils/apiClient';
// import SuccessToast from './SuccessToast';
// import ConfirmModal from './ConfirmModal';

// const UserTable = ({ data = [], isLoading = false }) => {
//   const auth = useAuth();
//   const { userData } = useUser();
  
//   // State for selected rows
//   const [selectedRows, setSelectedRows] = useState({});
  
//   // State for pagination and sorting
//   const [pagination, setPagination] = useState({
//     pageIndex: 0,
//     pageSize: 10,
//   });
//   const [sorting, setSorting] = useState([]);

//   // State for success toast
//   const [showToast, setShowToast] = useState(false);
//   const [toastMessage, setToastMessage] = useState('');

//   // State for confirmation modal
//   const [showConfirm, setShowConfirm] = useState(false);
//   const [confirmAction, setConfirmAction] = useState(null);

//   // Clear selected rows when data changes
//   useEffect(() => {
//     setSelectedRows({});
//   }, [data]);

//   // Handle remove action from dropdown menu
//   const handleRemoveTableButton = async ({ gsc_user, gsc_property, action }) => {
//     const token = auth.user?.access_token;
  
//     if (!token) {
//       console.error("Authorization token is missing");
//       return;
//     }
  
//     const requestData = {
//       gsc_user,
//       gsc_property,
//       action,
//       org: userData.o,
//       requestor: auth.user?.profile.sub,
//     };
  
//     try {
//       const response = await userManagementAPI.addUsers(token, [requestData]);
//       console.log("Response from API:", response);
//       setToastMessage(`User ${gsc_user} removal request submitted successfully`);
//       setShowToast(true);
//     } catch (error) {
//       console.error("Error sending data to API:", error);
//       alert(`Failed to submit removal request: ${error.message}`);
//     }
//   };

//   // Handle bulk remove from properties
//   const handleRemoveFromProperties = async () => {
//     const token = auth.user?.access_token;
    
//     if (!token) {
//       console.error("Authorization token is missing");
//       return;
//     }
  
//     const requestData = [];
  
//     // Collect data for selected rows
//     Object.keys(selectedRows).forEach((rowId) => {
//       if (selectedRows[rowId]) {
//         const row = table.getRow(rowId);
//         requestData.push({
//           gsc_user: row.original.email,
//           gsc_property: row.original.property,
//           action: 'remove from property',
//           org: userData.o,
//           requestor: auth.user?.profile.sub
//         });
//       }
//     });
  
//     if (requestData.length === 0) {
//       alert("Please select at least one row");
//       return;
//     }

//     // Show confirmation modal
//     setConfirmAction(() => async () => {
//       try {
//         const response = await userManagementAPI.addUsers(token, requestData);
//         console.log('Response from API:', response);
//         setToastMessage(`Successfully submitted removal request for ${requestData.length} user(s)`);
//         setShowToast(true);
//         setSelectedRows({}); // Clear selection
//       } catch (error) {
//         console.error('Error sending data to API:', error);
//         alert(`Failed to submit removal request: ${error.message}`);
//       }
//     });
//     setShowConfirm(true);
//   };

//   // Column Definitions with sorting and custom widths
//   const columnHelper = createColumnHelper();
//   const columns = useMemo(
//     () => [
//       columnHelper.display({
//         id: 'selection',
//         header: ({ table }) => (
//           <div style={{ textAlign: 'center', position: 'relative', left: '30%', display: 'list-item' }} className="hideLst">
//             <Form.Check
//               type="checkbox"
//               onChange={(e) => {
//                 if (e.target.checked) {
//                   const newSelectedRows = {};
//                   table.getRowModel().rows.forEach((row) => {
//                     newSelectedRows[row.id] = true;
//                   });
//                   setSelectedRows(newSelectedRows);
//                 } else {
//                   setSelectedRows({});
//                 }
//               }}
//               checked={
//                 Object.keys(selectedRows).length === table.getRowModel().rows.length &&
//                 table.getRowModel().rows.length > 0
//               }
//             />
//           </div>
//         ),
//         cell: (info) => (
//           <div style={{ textAlign: 'center', position: 'relative', left: '25%', display: 'list-item' }} className="hideLst">
//             <Form.Check
//               type="checkbox"
//               checked={!!selectedRows[info.row.id]}
//               onChange={(e) => {
//                 setSelectedRows((prev) => ({
//                   ...prev,
//                   [info.row.id]: e.target.checked,
//                 }));
//               }}
//             />
//           </div>
//         ),
//         size: 50,
//         enableSorting: false,
//       }),
//       columnHelper.accessor('name', { 
//         header: 'Name',
//         size: 150,
//         enableSorting: true,
//       }),
//       columnHelper.accessor('email', { 
//         header: 'Email',
//         size: 250,
//         enableSorting: true,
//       }),
//       columnHelper.accessor('permissions', { 
//         header: 'Permissions',
//         size: 120,
//         enableSorting: true,
//       }),
//       columnHelper.accessor('property', { 
//         header: 'Property',
//         size: 250,
//         enableSorting: true,
//       }),
//       columnHelper.accessor('domain', { 
//         header: 'Domain',
//         size: 150,
//         enableSorting: true,
//       }),
//       columnHelper.accessor('discovered', { 
//         header: 'Discovered',
//         size: 120,
//         enableSorting: true,
//       }),
//       columnHelper.accessor('added_by', { 
//         header: 'Added By',
//         size: 150,
//         enableSorting: true,
//       }),
//       columnHelper.display({
//         id: 'actions',
//         header: 'Remove',
//         size: 120,
//         enableSorting: false,
//         cell: (info) => (
//           <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
//             <Dropdown>
//               <Dropdown.Toggle variant="secondary" size="sm" id={`dropdown-${info.row.id}`}>
//                 Remove
//               </Dropdown.Toggle>

//               <Dropdown.Menu>
//                 <Dropdown.Item
//                   onClick={(e) => {
//                     e.stopPropagation();
//                     handleRemoveTableButton({
//                       gsc_user: info.row.original.email,
//                       gsc_property: info.row.original.property,
//                       action: "remove from property",
//                     });
//                   }}
//                 >
//                   From Property
//                 </Dropdown.Item>
//                 <Dropdown.Item
//                   onClick={(e) => {
//                     e.stopPropagation();
//                     handleRemoveTableButton({
//                       gsc_user: info.row.original.email,
//                       gsc_property: info.row.original.domain,
//                       action: "remove from domain",
//                     });
//                   }}
//                 >
//                   From Domain
//                 </Dropdown.Item>
//                 <Dropdown.Item
//                   onClick={(e) => {
//                     e.stopPropagation();
//                     handleRemoveTableButton({
//                       gsc_user: info.row.original.email,
//                       gsc_property: 'all',
//                       action: "remove from account",
//                     });
//                   }}
//                 >
//                   From Account
//                 </Dropdown.Item>
//               </Dropdown.Menu>
//             </Dropdown>
//           </div>
//         ),
//       }),
//     ],
//     [columnHelper, selectedRows]
//   );

//   // TanStack Table Instance
//   const table = useReactTable({
//     data,
//     columns,
//     getCoreRowModel: getCoreRowModel(),
//     getPaginationRowModel: getPaginationRowModel(),
//     getSortedRowModel: getSortedRowModel(),
//     onPaginationChange: setPagination,
//     onSortingChange: setSorting,
//     state: {
//       pagination,
//       sorting,
//     },
//   });

//   const pageSizes = [10, 25, 50, 100, 250, 500];

//   // Show empty state
//   if (!data || data.length === 0) {
//     return (
//       <Container className="text-center mt-5">
//         <p className="text-muted">No data available. Try selecting a different date or adjusting filters.</p>
//       </Container>
//     );
//   }

//   return (
//     <Container fluid style={{ paddingLeft: '15px', paddingRight: '15px' }}>
//       <Row className="mb-3 align-items-center">
//         <Col xs={12} md={6} className="mb-2 mb-md-0">
//           <div className="d-flex align-items-center">
//             <span className="me-2 text-muted">Show &nbsp;</span>
//             <Form.Select
//             value={pagination.pageSize}
//             onChange={(e) => {
//               setPagination({
//                 pageIndex: 0,
//                 pageSize: Number(e.target.value),
//               });
//             }}
//             style={{ width: 'auto' }}
//           >
//             {pageSizes.map((size) => (
//               <option key={size} value={size}>
//                 {size}
//               </option>
//             ))}
//           </Form.Select>
//             <span className="ms-2 text-muted">&nbsp; entries</span>
//           </div>
//         </Col>
//         <Col xs={12} md={6}>
//           <div className="d-flex justify-content-md-end">
//             <span className="me-3 align-self-center text-muted">
//               Page {pagination.pageIndex + 1} of {table.getPageCount()}&nbsp;&nbsp;
//             </span>
//             <Button
//               variant="outline-primary"
//               size="sm"
//               onClick={() => table.previousPage()}
//               disabled={!table.getCanPreviousPage()}
//             >
//               Previous
//             </Button>&nbsp;
//             <Button
//               variant="outline-primary"
//               size="sm"
//               onClick={() => table.nextPage()}
//               disabled={!table.getCanNextPage()}
//             >
//               Next
//             </Button>
//           </div>
//         </Col>
//       </Row>

//       <div style={{ width: '100%', overflowX: 'auto', paddingBottom: '110px' }}>
//         <Table striped bordered hover style={{ 
//           width: '100%', 
//           tableLayout: 'fixed',
//           minWidth: '1200px'
//         }}>
//           <thead style={{ backgroundColor: '#343a40', color: 'white' }}>
//             {table.getHeaderGroups().map((headerGroup) => (
//               <tr key={headerGroup.id}>
//                 {headerGroup.headers.map((header) => (
//                   <th 
//                     key={header.id}
//                     style={{ 
//                       width: header.column.columnDef.size ? `${header.column.columnDef.size}px` : 'auto',
//                       textAlign: 'center',
//                       verticalAlign: 'middle',
//                       cursor: header.column.getCanSort() ? 'pointer' : 'default',
//                       userSelect: 'none',
//                     }}
//                     onClick={header.column.getToggleSortingHandler()}
//                   >
//                     <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
//                       {header.isPlaceholder
//                         ? null
//                         : flexRender(header.column.columnDef.header, header.getContext())}
//                       {header.column.getCanSort() && (
//                         <span style={{ fontSize: '0.8em' }}>
//                           {header.column.getIsSorted() === 'asc' ? '▲' : 
//                            header.column.getIsSorted() === 'desc' ? '▼' : 
//                            '⇅'}
//                         </span>
//                       )}
//                     </div>
//                   </th>
//                 ))}
//               </tr>
//             ))}
//           </thead>
//           <tbody>
//             {table.getRowModel().rows.map((row) => (
//               <tr key={row.id}>
//                 {row.getVisibleCells().map((cell) => {
//                   const isActionsColumn = cell.column.id === 'actions';
                  
//                   return (
//                     <td 
//                       key={cell.id}
//                       style={{
//                         textAlign: 'center',
//                         verticalAlign: 'middle',
//                         overflow: isActionsColumn ? 'visible' : 'hidden',
//                         textOverflow: isActionsColumn ? 'clip' : 'ellipsis',
//                         whiteSpace: isActionsColumn ? 'normal' : 'nowrap',
//                         padding: '8px',
//                         position: isActionsColumn ? 'relative' : 'static',
//                       }}
//                       title={!isActionsColumn ? (cell.getValue()?.toString() || '') : ''}
//                     >
//                       {isActionsColumn ? (
//                         flexRender(cell.column.columnDef.cell, cell.getContext())
//                       ) : (
//                         <div style={{
//                           overflow: 'auto',
//                           maxHeight: '60px',
//                           textAlign: 'center',
//                         }}>
//                           {flexRender(cell.column.columnDef.cell, cell.getContext())}
//                         </div>
//                       )}
//                     </td>
//                   );
//                 })}
//               </tr>
//             ))}
//           </tbody>
//         </Table>
//       </div>

//       <Row className="mt-3">
//         <Col>
//           <Button 
//             variant="danger" 
//             onClick={handleRemoveFromProperties}
//             disabled={Object.keys(selectedRows).length === 0}
//           >
//             Remove from Properties ({Object.keys(selectedRows).filter(k => selectedRows[k]).length})
//           </Button>
//         </Col>
//       </Row>

//       <Row className="mt-3 text-muted" style={{textAlign: 'justify'}}>
//         <Col>
//           <span>
//             Page {pagination.pageIndex + 1} of {table.getPageCount()} | 
//             Showing {table.getRowModel().rows.length} of {data.length} results
//           </span>
//         </Col>
//         <Col className="d-flex justify-content-end">
//           <Button
//             variant="outline-primary"
//             onClick={() => table.previousPage()}
//             disabled={!table.getCanPreviousPage()}
//           >
//             Previous
//           </Button>&nbsp;
//           <Button
//             variant="outline-primary"
//             onClick={() => table.nextPage()}
//             disabled={!table.getCanNextPage()}
//           >
//             Next
//           </Button>
//         </Col>
//       </Row>
      
//       <SuccessToast 
//         show={showToast}
//         onHide={() => setShowToast(false)}
//         message={toastMessage}
//         duration={4000}
//       />
      
//       <ConfirmModal
//         show={showConfirm}
//         onHide={() => setShowConfirm(false)}
//         onConfirm={confirmAction}
//         title="Confirm Removal"
//         message={`Are you sure you want to remove ${Object.keys(selectedRows).filter(k => selectedRows[k]).length} user(s) from their properties?`}
//         confirmText="Remove"
//         cancelText="Cancel"
//       />
//     </Container>
//   );
// };

// export default UserTable;

